import { useId } from "react";
import type { LucideIcon } from "lucide-react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export type KpiVariant = "default" | "info" | "success" | "warning" | "danger" | "neutral";

export type KpiDelta = {
  value: number;
  label?: string;
  inverse?: boolean;
};

export type KpiCardProps = {
  label: string;
  value: string | number;
  helper?: string;
  delta?: KpiDelta;
  sparkline?: number[];
  icon?: LucideIcon;
  variant?: KpiVariant;
  isLoading?: boolean;
  onClick?: () => void;
  className?: string;
  testId?: string;
};

const VARIANT_STYLES: Record<KpiVariant, {
  iconBg: string;
  iconFg: string;
  accent: string;
  glow: string;
  spark: string;
}> = {
  default: { iconBg: "bg-indigo-500/10", iconFg: "text-indigo-600 dark:text-indigo-400", accent: "from-indigo-500 to-violet-500", glow: "bg-indigo-500/5",  spark: "#6366f1" },
  info:    { iconBg: "bg-blue-500/10",   iconFg: "text-blue-600 dark:text-blue-400",     accent: "from-blue-500 to-cyan-500",     glow: "bg-blue-500/5",    spark: "#3b82f6" },
  success: { iconBg: "bg-emerald-500/10",iconFg: "text-emerald-600 dark:text-emerald-400",accent: "from-emerald-500 to-green-500", glow: "bg-emerald-500/5", spark: "#10b981" },
  warning: { iconBg: "bg-amber-500/10",  iconFg: "text-amber-600 dark:text-amber-400",   accent: "from-amber-500 to-orange-500",  glow: "bg-amber-500/5",   spark: "#f59e0b" },
  danger:  { iconBg: "bg-red-500/10",    iconFg: "text-red-600 dark:text-red-400",       accent: "from-red-500 to-rose-500",      glow: "bg-red-500/5",     spark: "#ef4444" },
  neutral: { iconBg: "bg-muted",         iconFg: "text-muted-foreground",                 accent: "from-slate-400 to-slate-500",   glow: "bg-slate-500/5",   spark: "#94a3b8" },
};

function deltaTone(value: number, inverse: boolean): "up" | "down" | "flat" {
  if (value === 0) return "flat";
  const positive = value > 0;
  if (inverse) return positive ? "down" : "up";
  return positive ? "up" : "down";
}

function DeltaBadge({ delta }: { delta: KpiDelta }) {
  const tone = deltaTone(delta.value, delta.inverse ?? false);
  const Icon = tone === "up" ? ArrowUpRight : tone === "down" ? ArrowDownRight : Minus;
  const classes =
    tone === "up"   ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" :
    tone === "down" ? "bg-red-500/10 text-red-700 dark:text-red-400" :
                      "bg-muted text-muted-foreground";
  const sign = delta.value > 0 ? "+" : "";
  return (
    <span className={cn("inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums", classes)}>
      <Icon className="h-2.5 w-2.5" />
      {sign}{delta.value.toFixed(1)}%
      {delta.label ? <span className="font-normal ml-1 opacity-70">{delta.label}</span> : null}
    </span>
  );
}

function Sparkline({ data, color }: { data: number[]; color: string }) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  const series = data.map((v, i) => ({ i, v }));
  return (
    <div className="absolute inset-x-0 bottom-0 h-12 pointer-events-none opacity-90">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={`spark-${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={1.75}
            fill={`url(#spark-${id})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function KpiCard({
  label, value, helper, delta, sparkline, icon: Icon,
  variant = "default", isLoading, onClick, className, testId,
}: KpiCardProps) {
  const styles = VARIANT_STYLES[variant];
  const interactive = !!onClick;
  const hasSpark = !!(sparkline && sparkline.length > 1);

  return (
    <Card
      className={cn(
        "group relative overflow-hidden border-border/60 transition-all duration-200",
        interactive && "cursor-pointer hover:shadow-lg hover:-translate-y-0.5 hover:border-foreground/20",
        className,
      )}
      onClick={onClick}
      data-testid={testId}
    >
      {/* accent bar at top */}
      <div className={cn("absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r opacity-80", styles.accent)} />
      {/* soft radial glow */}
      <div className={cn("absolute -top-12 -right-12 h-32 w-32 rounded-full blur-2xl opacity-60 pointer-events-none", styles.glow)} />

      <CardContent className="relative p-5 pb-6">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground truncate">
            {label}
          </p>
          {Icon ? (
            <div className={cn("rounded-lg p-1.5 shrink-0", styles.iconBg)}>
              <Icon className={cn("h-4 w-4", styles.iconFg)} />
            </div>
          ) : null}
        </div>

        <div className="mt-3 min-h-[2.5rem]">
          {isLoading ? (
            <Skeleton className="h-9 w-28" />
          ) : (
            <p className="text-3xl font-bold tracking-tight tabular-nums truncate leading-none">{value}</p>
          )}
        </div>

        <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground min-h-[1.25rem]">
          {!isLoading && delta ? <DeltaBadge delta={delta} /> : null}
          {!isLoading && helper ? <span className="truncate">{helper}</span> : null}
        </div>

        {/* sparkline anchored at bottom of card, behind content via positioning */}
        {hasSpark && !isLoading ? <Sparkline data={sparkline!} color={styles.spark} /> : null}
      </CardContent>
    </Card>
  );
}

export default KpiCard;
